import { syncLocalSession } from '../api/abs';
import { AbsError } from '../api/client';
import { getSession, type AuthSession } from '../api/session';
import { PlayMethod } from '../api/types';
import { dbGet, dbUpdate } from '../lib/db';
import { getDeviceId } from '../lib/device';

// Listening done without an Audiobookshelf playback session (a downloaded
// book, which plays from this device, or the direct-file fallback) still
// counts in the server's listening stats: the time is collected here as
// "local sessions" in IndexedDB, per account, and uploaded with
// `POST /api/session/local` once the device is online. A PWA addition:
// Flutter reports nothing for local playback.
//
// A local session is a chunk of listening to one book. The engine adds to
// the open chunk while it plays and closes it when playback pauses, stops or
// switches books; closed chunks are uploaded (each once, under its own id)
// and then deleted. A reload closes every chunk.

export interface LocalSessionBook {
  itemId: string;
  title: string;
  author: string;
  /** Seconds. */
  duration: number;
  libraryId?: string | null;
  /** ABS book (media) id, when known. */
  bookId?: string | null;
}

/** The stored chunk, in the shape `POST /api/session/local` takes (an ABS PlaybackSession). */
export interface LocalListeningSession {
  id: string;
  libraryItemId: string;
  libraryId: string | null;
  bookId: string | null;
  episodeId: null;
  mediaType: 'book';
  displayTitle: string;
  displayAuthor: string;
  duration: number;
  playMethod: number;
  mediaPlayer: 'html5';
  /** Position (seconds) where the chunk began. */
  startTime: number;
  /** Position (seconds) at its last update. */
  currentTime: number;
  /** Seconds listened. */
  timeListening: number;
  startedAt: number;
  updatedAt: number;
  /** Local calendar day the chunk started (`YYYY-MM-DD`), which ABS stats group by. */
  date: string;
  dayOfWeek: string;
}

type Account = Pick<AuthSession, 'serverUrl' | 'userId'>;

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const pad2 = (n: number) => String(n).padStart(2, '0');

/** IndexedDB (`kv`) key of an account's pending local sessions. */
export function localSessionsKey(account: Account): string {
  return `localSessions:${account.serverUrl}|${account.userId}`;
}

function localDay(ms: number): { date: string; dayOfWeek: string } {
  const d = new Date(ms);
  return { date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`, dayOfWeek: WEEKDAYS[d.getDay()] ?? 'Sunday' };
}

function newId(): string {
  try {
    return `play_local_${crypto.randomUUID()}`;
  } catch {
    return `play_local_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  }
}

/** A new chunk of `listened` seconds that ended at `currentTime` (pure). */
export function newLocalSession(id: string, book: LocalSessionBook, listened: number, currentTime: number, now: number): LocalListeningSession {
  const startedAt = now - Math.round(listened * 1000);
  return {
    id,
    libraryItemId: book.itemId,
    libraryId: book.libraryId ?? null,
    bookId: book.bookId ?? null,
    episodeId: null,
    mediaType: 'book',
    displayTitle: book.title,
    displayAuthor: book.author,
    duration: book.duration,
    playMethod: PlayMethod.Local,
    mediaPlayer: 'html5',
    startTime: Math.max(0, currentTime - listened),
    currentTime,
    timeListening: listened,
    startedAt,
    updatedAt: now,
    ...localDay(startedAt),
  };
}

/** The chunk being added to: account key, book and session id. */
let open: { key: string; itemId: string; id: string } | null = null;

/**
 * Adds `listened` seconds of playback without a server session to the open
 * chunk of `book` for `account` (a new chunk when none is open for it).
 */
export async function recordLocalListening(account: Account, book: LocalSessionBook, listened: number, currentTime: number, now = Date.now()): Promise<void> {
  if (!(listened > 0)) return;
  const key = localSessionsKey(account);
  if (!open || open.key !== key || open.itemId !== book.itemId) open = { key, itemId: book.itemId, id: newId() };
  const { id } = open;
  try {
    await dbUpdate<LocalListeningSession[]>('kv', key, (prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      const index = list.findIndex((s) => s.id === id);
      const existing = index >= 0 ? list[index] : undefined;
      if (existing) {
        list[index] = { ...existing, timeListening: existing.timeListening + listened, currentTime, updatedAt: now, duration: book.duration || existing.duration };
      } else {
        list.push(newLocalSession(id, book, listened, currentTime, now));
      }
      return list;
    });
  } catch (err) {
    console.warn('[player] could not save offline listening time', err);
  }
}

/** Ends the open chunk (pause, stop, another book): it is uploaded on the next flush. */
export function closeLocalListening(): void {
  open = null;
}

const isTransient = (err: unknown) => err instanceof AbsError && (err.isNetworkError || err.status === 401 || err.status === 408 || err.status === 429 || err.status >= 500);

let flushing: Promise<number> | null = null;

/**
 * Uploads the signed-in account's closed chunks (`POST /api/session/local`)
 * and deletes each one the server took. Stops at the first transient failure
 * (offline, 5xx, expired session) and keeps the rest for the next flush; a
 * chunk the server refuses outright (book deleted) is dropped. Returns how
 * many were uploaded.
 */
export function flushLocalListening(): Promise<number> {
  if (flushing) return flushing;
  flushing = (async () => {
    const session = getSession();
    if (!session || (typeof navigator !== 'undefined' && navigator.onLine === false)) return 0;
    const key = localSessionsKey(session);
    let pending: LocalListeningSession[] = [];
    try {
      const stored = await dbGet<LocalListeningSession[]>('kv', key);
      pending = Array.isArray(stored) ? stored.filter((s) => s.id !== open?.id && s.timeListening > 0) : [];
    } catch {
      return 0;
    }
    let sent = 0;
    for (const chunk of pending) {
      try {
        await syncLocalSession({
          ...chunk,
          timeListening: Math.round(chunk.timeListening),
          deviceInfo: { deviceId: getDeviceId(), clientName: 'Plume PWA' },
        } as unknown as Record<string, unknown>);
        sent++;
      } catch (err) {
        if (isTransient(err)) break;
        console.warn('[player] the server refused offline listening time; dropping it', err);
      }
      try {
        await dbUpdate<LocalListeningSession[]>('kv', key, (prev) => {
          const rest = (Array.isArray(prev) ? prev : []).filter((s) => s.id !== chunk.id);
          return rest.length > 0 ? rest : undefined;
        });
      } catch {
        break;
      }
    }
    return sent;
  })().finally(() => {
    flushing = null;
  });
  return flushing;
}

/** Test hook. */
export function __resetLocalListeningForTests(): void {
  open = null;
  flushing = null;
}
